/**
 * Cash Refunds queue.
 *
 * One row per refund: cash loaded onto a wristband at a booth, on its way back
 * to the attendee as a bank transfer.
 *
 * A refund sits at 'processing' from the moment the money leaves the wristband
 * until the payment file it goes out in is marked sent, which is done in Refund
 * Manager -> ACH Payouts. This screen is the wider view of the same refunds,
 * and the one thing ops does here is cancel: a refund going nowhere — wrong
 * bank details, an attendee who changed their mind — puts the money back on the
 * wristband and frees the band for another attempt.
 *
 * Once a refund is sent it cannot be canceled: the bank has the file.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoaderFunctionArgs, useLoaderData, useRevalidator } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import { toast } from "react-hot-toast";
import moment from "moment";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { isAdminUser } from "../../providers/AuthProvider";
import { organizationStore } from "../../store/organization";
import { useEventStore } from "../../store/event";
import { CASH_REFUNDS_QUERY } from "./queries";
import { CashRefundRow, CashRefundStatus, IN_FLIGHT } from "./types";
import { cancelCashRefund } from "./api";

const client = createApolloClient();

const money = (cents: number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    (cents || 0) / 100
  );

/** Money off a wristband that neither reached the bank nor went back. */
const needsAttention = (row: CashRefundRow) =>
  row.needs_manual_fix ||
  (row.hold_applied && !row.balance_restored && !IN_FLIGHT.includes(row.status) && row.status !== "sent");

const STATUS_LABELS: Record<CashRefundStatus, string> = {
  // 'processing' is a refund waiting for the next payment file, which can be
  // days. "Sending" made that look like something was stuck.
  processing: "Queued",
  sent: "Paid",
  failed: "Failed",
  canceled: "Canceled",
};

const STATUS_CLASSES: Record<CashRefundStatus, string> = {
  processing: "bg-amber-100 text-amber-800",
  sent: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  canceled: "bg-gray-200 text-gray-700",
};

/**
 * Scope: events belong to organizations, so a non-admin only ever sees refunds
 * for the organization they are currently in. Admins see everything.
 */
export const cashRefundsLoader = async ({}: LoaderFunctionArgs) => {
  const rawOrgId = organizationStore.getState().organizationId;
  const organizationId = parseInt(rawOrgId !== null ? rawOrgId.toString() : "", 10);
  const admin = isAdminUser();

  // Fail closed. parseInt of an unhydrated store value is NaN, and letting
  // that fall through to an empty `where` showed a non-admin every
  // organization's refunds. Hasura row permissions should enforce this too —
  // this filter is defence in depth, not the boundary.
  const where: any = {};
  if (!admin) {
    where.event = {
      organization_id: { _eq: Number.isFinite(organizationId) && organizationId > 0 ? organizationId : -1 },
    };
  }

  const { data } = await client.query({
    query: CASH_REFUNDS_QUERY,
    variables: { where },
    fetchPolicy: "network-only",
  });

  return { refunds: (data?.cashless_refund_requests ?? []) as CashRefundRow[] };
};

export const CashRefundList = () => {
  const { refunds } = useLoaderData() as { refunds: CashRefundRow[] };
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const admin = isAdminUser();

  const [statusFilter, setStatusFilter] = useState<
    "all" | "in_flight" | "needs_attention" | CashRefundStatus
  >("in_flight");
  const [search, setSearch] = useState("");
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<CashRefundRow | null>(null);

  // The event picked in the top bar filters the grid, matching the other
  // resource pages. Read through the hook so switching events re-renders.
  const selectedEventId = useEventStore((state) => state.eventId);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return refunds.filter((row) => {
      if (selectedEventId && row.event_id !== selectedEventId) return false;
      if (statusFilter === "in_flight" && !IN_FLIGHT.includes(row.status)) return false;
      // Money that is off a band with no payout and no restore. These are the
      // rows that need a human, so they get their own view.
      if (statusFilter === "needs_attention" && !needsAttention(row)) return false;
      if (
        statusFilter !== "all" &&
        statusFilter !== "in_flight" &&
        statusFilter !== "needs_attention" &&
        row.status !== statusFilter
      ) {
        return false;
      }
      if (!term) return true;
      return [row.uid, row.recipient_email, row.recipient_name, row.guid, row.event?.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [refunds, search, statusFilter, selectedEventId]);

  const totals = useMemo(() => {
    // "Held" means the money is off a wristband right now: everything still in
    // flight, PLUS anything that ended without the balance being restored.
    // Leaving the second group out hid exactly the rows that need fixing.
    const held = rows.filter(
      (r) => IN_FLIGHT.includes(r.status) || (r.hold_applied && !r.balance_restored && r.status !== "sent")
    );
    return {
      count: rows.length,
      heldCount: held.length,
      heldAmount: held.reduce((sum, r) => sum + (r.gross_amount || 0), 0),
      attentionCount: rows.filter(needsAttention).length,
      sentAmount: rows
        .filter((r) => r.status === "sent")
        .reduce((sum, r) => sum + (r.amount || 0), 0),
    };
  }, [rows]);

  const handleCancel = useCallback(async () => {
    if (!pendingCancel) return;
    setBusyGuid(pendingCancel.guid);
    try {
      const result = await cancelCashRefund(pendingCancel.guid, "canceled from dashboard");
      toast.success(
        result.restored
          ? `${money(pendingCancel.gross_amount)} put back on ${pendingCancel.uid}`
          : "Canceled, but the balance needs a manual fix — check the logs"
      );
      setPendingCancel(null);
      revalidator.revalidate();
    } catch (err: any) {
      toast.error(err?.message || "Could not cancel that refund");
    } finally {
      setBusyGuid(null);
    }
  }, [pendingCancel, revalidator]);

  const columnDefs = useMemo<ColDef<CashRefundRow>[]>(
    () => [
      {
        headerName: "Requested",
        field: "created_at",
        width: 160,
        valueFormatter: (p) => (p.value ? moment(p.value).format("MM/DD/YY h:mm a") : ""),
        sort: "desc",
      },
      { headerName: "Event", valueGetter: (p) => p.data?.event?.name ?? p.data?.event_id, width: 180 },
      { headerName: "Wristband", field: "uid", width: 140 },
      {
        headerName: "Status",
        field: "status",
        width: 150,
        cellRenderer: (p: ICellRendererParams<CashRefundRow>) => {
          const status = p.data?.status as CashRefundStatus;
          if (!status) return null;
          return (
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${STATUS_CLASSES[status]}`}
            >
              {STATUS_LABELS[status]}
            </span>
          );
        },
      },
      {
        headerName: "Delivery",
        field: "delivery_status",
        width: 130,
        // Deliberately not shown as a success tick: "delivered" only means the
        // message arrived. A bounce is the one worth catching the eye.
        cellRenderer: (p: ICellRendererParams<CashRefundRow>) => {
          const delivery = p.data?.delivery_status;
          if (!delivery) return <span className="text-xs text-gray-400">—</span>;
          if (delivery === "FAILED") {
            return (
              <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                Bounced
              </span>
            );
          }
          return (
            <span className="text-xs text-gray-600">
              {delivery === "SUCCEEDED" ? "Delivered" : "Pending"}
            </span>
          );
        },
      },
      {
        headerName: "Off the band",
        field: "gross_amount",
        width: 130,
        valueFormatter: (p) => money(p.value),
      },
      {
        headerName: "To attendee",
        field: "amount",
        width: 130,
        valueFormatter: (p) => money(p.value),
      },
      { headerName: "Name", field: "recipient_name", width: 160 },
      {
        headerName: "Sent to",
        width: 220,
        valueGetter: (p) =>
          p.data?.delivery_method === "PHONE" ? p.data?.recipient_phone : p.data?.recipient_email,
      },
      {
        headerName: "Phone verified",
        field: "phone_verified",
        width: 140,
        valueFormatter: (p) => (p.value ? "Yes" : "No contact on file"),
      },
      {
        headerName: "Balance restored",
        field: "balance_restored",
        width: 150,
        valueFormatter: (p) => (p.value ? "Yes" : ""),
      },
      {
        headerName: "Needs attention",
        field: "needs_manual_fix",
        width: 150,
        valueFormatter: (p) => (p.value ? "Yes" : ""),
      },
      { headerName: "Problem", field: "failure_reason", width: 220 },
      {
        headerName: "Actions",
        width: 200,
        pinned: "right",
        // Every header sorts (house convention). Sorting here floats the rows
        // that actually have an action — stuck 'processing' ones — to the top.
        valueGetter: (p) => (p.data && IN_FLIGHT.includes(p.data.status) ? 1 : 0),
        filter: false,
        cellRenderer: (p: ICellRendererParams<CashRefundRow>) => {
          const row = p.data;
          if (!row) return null;
          const busy = busyGuid === row.guid;
          // Only a refund still waiting for a payment file can be taken back:
          // once the bank has it, the money is gone.
          const canCancel = admin && row.status === "processing";

          return (
            <div className="flex items-center gap-2">
              {canCancel && (
                <button
                  disabled={busy}
                  onClick={() => setPendingCancel(row)}
                  className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [admin, busyGuid]
  );

  // Refresh while anything is still moving, so ops does not sit on a stale page.
  useEffect(() => {
    if (!refunds.some((r) => IN_FLIGHT.includes(r.status))) return;
    const timer = window.setInterval(() => revalidator.revalidate(), 60000);
    return () => window.clearInterval(timer);
  }, [refunds, revalidator]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <Breadcrumbs />

      <div className="mt-4 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Cash Refunds</h1>
          <p className="mt-1 text-sm text-gray-600">
            Cash loaded at a booth, on its way back to attendees as a bank transfer.
          </p>
        </div>
        <div className="mt-4 flex gap-2 sm:mt-0">
          <SecondaryButton
            onClick={() =>
              // Not allColumns: the reference is the bearer credential for a
              // refund and request_ip is personal data — neither belongs in a
              // spreadsheet that gets emailed around.
              gridRef.current?.exportDataAsCsv({
                fileName: "CashRefunds.csv",
                columnSeparator: ",",
              })
            }
          >
            Export CSV
          </SecondaryButton>
          <PrimaryButton onClick={() => revalidator.revalidate()}>Refresh</PrimaryButton>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-gray-600">Requests shown</p>
          <p className="text-2xl font-semibold text-gray-900">{totals.count}</p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-gray-600">Held off wristbands</p>
          <p className="text-2xl font-semibold text-gray-900">{money(totals.heldAmount)}</p>
          <p className="text-xs text-gray-500">
            {totals.heldCount} held
            {totals.attentionCount > 0 ? ` · ${totals.attentionCount} need attention` : ""}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-gray-600">Paid out</p>
          <p className="text-2xl font-semibold text-gray-900">{money(totals.sentAmount)}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search wristband, name, email or reference"
          className="w-80 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm"
        >
          <option value="in_flight">In progress</option>
          <option value="needs_attention">Needs attention</option>
          <option value="all">All</option>
          <option value="processing">Queued</option>
          <option value="sent">Paid</option>
          <option value="failed">Failed</option>
          <option value="canceled">Canceled</option>
        </select>
      </div>

      <div className={"ag-theme-quartz mt-4"} style={{ height: 640, width: "100%" }}>
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={100}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
          onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>

      <ConfirmDialog
        open={!!pendingCancel}
        title="Cancel this refund?"
        busy={!!busyGuid}
        confirmLabel="Cancel refund"
        cancelLabel="Keep it"
        message={
          pendingCancel ? (
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                {money(pendingCancel.gross_amount)} goes back onto wristband{" "}
                <strong>{pendingCancel.uid}</strong>, and the attendee can request a
                refund again.
              </p>
              <p className="text-gray-500">
                {pendingCancel.recipient_name || "No name"} ·{" "}
                {pendingCancel.recipient_email || "no email"}
              </p>
            </div>
          ) : null
        }
        onConfirm={handleCancel}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
};
